/**
 * The controls Tab can reach inside `root` right now, in document order.
 *
 * There is no tabbing library in this tree, so this is the one place the
 * question is answered rather than a selector per screen. A selector alone
 * cannot answer it: whether a control takes focus depends on its tab index,
 * on a disabled fieldset around it, and on whether it or anything above it is
 * hidden or inert -- none of which is an attribute of the control itself. So
 * the selector only gathers the candidates and each is then asked properly.
 *
 * A group of radio buttons sharing a name is one stop, as it is to the
 * keyboard: its checked member, or its first when none is checked (Tab enters
 * an unchecked group at its first member and Shift+Tab at its last; either is
 * the group, which is what `sameStop` asks).
 *
 * Positive tab indexes are kept in document order rather than sorted ahead;
 * nothing in this application sets one, so this is not claimed to be the
 * browser's order where one is set.
 */
export function tabStops(root: HTMLElement): HTMLElement[] {
  const reached = Array.from(root.querySelectorAll<HTMLElement>(CANDIDATES)).filter((el) =>
    reachable(el, root),
  )
  return reached.filter((el) => {
    const group = radioGroup(el, root)
    if (group.length < 2) return true
    const stop = group.find((radio) => radio.checked) ?? group[0]
    return el === stop
  })
}

/**
 * Whether `at` is the stop `stop` stands for: the same control, or another
 * member of the same radio group.
 */
export function sameStop(at: Element | null, stop: HTMLElement, root: HTMLElement): boolean {
  if (at === stop) return true
  if (!(at instanceof HTMLInputElement)) return false
  return radioGroup(stop, root).includes(at)
}

/**
 * The control to focus for `stop` when moving onto it forwards or backwards:
 * the stop itself, except an unchecked radio group, which the keyboard enters
 * at its last member going backwards.
 */
export function landing(stop: HTMLElement, backwards: boolean, root: HTMLElement): HTMLElement {
  const group = radioGroup(stop, root)
  if (!backwards || group.length < 2 || group.some((radio) => radio.checked)) return stop
  return group[group.length - 1] ?? stop
}

/** The reachable radios sharing `el`'s name and form, `el` among them, or none. */
function radioGroup(el: HTMLElement, root: HTMLElement): HTMLInputElement[] {
  if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) return []
  return Array.from(root.querySelectorAll<HTMLInputElement>('input[type="radio"]')).filter(
    (radio) => radio.name === el.name && radio.form === el.form && reachable(radio, root),
  )
}

const CANDIDATES =
  'a[href], area[href], button, input, select, textarea, iframe, summary, ' +
  '[contenteditable=""], [contenteditable="true"], [tabindex]'

function reachable(el: HTMLElement, root: HTMLElement): boolean {
  // The element's own answer, which already says -1 for a button or input
  // given tabindex="-1" and 0 for one given nothing.
  if (el.tabIndex < 0) return false
  // `:disabled` is true of a control inside a disabled fieldset as well as of
  // one carrying the attribute itself.
  if (el.matches(':disabled')) return false
  // Hidden (the attribute, a hidden input, or any `display: none` above it)
  // is asked of the computed style, which is what decides it; inert is not a
  // style, so it is asked of the ancestors.
  if (el.closest('[inert]')) return false
  for (let at: Element | null = el; at && at !== root.parentElement; at = at.parentElement) {
    if (getComputedStyle(at).display === 'none') return false
    // The collapsed content of a closed details is not rendered, whatever its
    // computed style says; only the details' own first summary stays.
    const parent = at.parentElement
    if (parent instanceof HTMLDetailsElement && !parent.open && at !== firstSummary(parent)) {
      return false
    }
  }
  const visibility = getComputedStyle(el).visibility
  return visibility !== 'hidden' && visibility !== 'collapse'
}

function firstSummary(details: HTMLDetailsElement): Element | null {
  return Array.from(details.children).find((child) => child.tagName === 'SUMMARY') ?? null
}
